import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from './app';
import History from './utils/History';
import Pane from './utils/Pane';
import DiscussionPage from './components/DiscussionPage';
import HeaderPrimary from './components/HeaderPrimary';
import HeaderSecondary from './components/HeaderSecondary';
import GlobalSearch from './components/GlobalSearch';
import DiscussionRenamedNotification from './components/DiscussionRenamedNotification';
import CommentPost from './components/CommentPost';
import DiscussionRenamedPost from './components/DiscussionRenamedPost';
import routes, { makeRouteHelpers } from './routes';
import Application from '../common/Application';
import Button from '../common/components/Button';
import Navigation from '../common/components/Navigation';
import NotificationListState from './states/NotificationListState';
import GlobalSearchState from './states/GlobalSearchState';
import DiscussionListState from './states/DiscussionListState';
import ComposerState from './states/ComposerState';
import isSafariMobile from './utils/isSafariMobile';
import routeInternalLinks from './utils/routeInternalLinks';
import extractText from '../common/utils/extractText';
import drainPrefetchQueue from '../common/utils/drainPrefetchQueue';
import Notices from './components/Notices';
import Footer from './components/Footer';
import SearchManager from '../common/SearchManager';
export default class ForumApplication extends Application {
  constructor() {
    super();
    /**
     * A map of notification types to their components.
     */
    _defineProperty(this, "notificationComponents", {
      discussionRenamed: DiscussionRenamedNotification
    });
    /**
     * A map of post types to their components.
     */
    _defineProperty(this, "postComponents", {
      comment: CommentPost,
      discussionRenamed: DiscussionRenamedPost
    });
    /**
     * An object which controls the state of the page's side pane.
     */
    _defineProperty(this, "pane", null);
    /**
     * The app's history stack, which keeps track of which routes the user visits
     * so that they can easily navigate back to the previous route.
     */
    _defineProperty(this, "history", new History());
    /**
     * An object which controls the state of the user's notifications.
     */
    _defineProperty(this, "notifications", new NotificationListState());
    /**
     * An object which stores the global search state and manages search capabilities.
     */
    _defineProperty(this, "search", new SearchManager(new GlobalSearchState()));
    /**
     * An object which controls the state of the composer.
     */
    _defineProperty(this, "composer", new ComposerState());
    /**
     * An object which controls the state of the cached discussion list, which
     * is used in the index page and the slideout pane.
     */
    _defineProperty(this, "discussions", new DiscussionListState({}));
    _defineProperty(this, "route", void 0);
    _defineProperty(this, "data", void 0);
    /**
     * Whether we have already alerted the user that the forum's assets have been
     * updated since they loaded the page. Ensures the prompt is shown only once.
     */
    _defineProperty(this, "assetsRevisionAlertShown", false);
    routes(this);
    this.route = Object.assign(Object.getPrototypeOf(Object.getPrototypeOf(this)).route.bind(this), makeRouteHelpers(this));
  }

  /**
   * @inheritdoc
   */
  mount() {
    var _document$getElementB;
    // Get the configured default route and update that route's path to be '/'.
    // Push the homepage as the first route, so that the user will always be
    // able to click on the 'back' button to go home, regardless of which page
    // they started on.
    const defaultRoute = this.forum.attribute('defaultRoute');
    let defaultAction = 'index';
    for (const i in this.routes) {
      if (this.routes[i].path === defaultRoute) defaultAction = i;
    }
    this.routes[defaultAction].path = '/';
    this.history.push(defaultAction, extractText(this.translator.trans('core.forum.header.back_to_index_tooltip')), '/');
    this.pane = new Pane(document.getElementById('app'));
    m.route.prefix = '';
    super.mount(this.forum.attribute('basePath'));

    // We mount navigation and header components after the page, so components
    // like the back button can access the updated state when rendering.
    m.mount(document.getElementById('app-navigation'), {
      view: () => m(Navigation, {
        className: "App-backControl",
        drawer: true,
        search: m(GlobalSearch, {
          state: this.search.state,
          navigation: true
        })
      })
    });
    m.mount(document.getElementById('header-navigation'), Navigation);
    m.mount(document.getElementById('header-primary'), HeaderPrimary);
    m.mount(document.getElementById('header-secondary'), HeaderSecondary);
    m.mount(document.getElementById('notices'), Notices);
    m.mount(document.getElementById('footer'), Footer);

    // Registered before the listeners below, which are bound to elements that
    // are not on every page — so a missing one cannot stop this from running.
    routeInternalLinks();

    // Route the home link back home when clicked. We do not want it to register
    // if the user is opening it in a new tab, however.
    (_document$getElementB = document.getElementById('home-link')) == null || _document$getElementB.addEventListener('click', e => {
      var _app$session$user;
      if (e.ctrlKey || e.metaKey || e.button === 1) return;
      e.preventDefault();
      app.history.home();

      // Reload the current user so that their unread notification count is refreshed.
      const userId = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id();
      if (userId) {
        app.store.find('users', userId);
        m.redraw();
      }
    });
    if (isSafariMobile()) {
      $(() => {
        $('.App').addClass('mobile-safari');
      });
    }

    // Once the app is mounted, warm any registered code-split chunks in the
    // background so later navigations don't pay for them on the critical path.
    drainPrefetchQueue(this.prefetch);
  }

  /**
   * Check whether or not the user is currently viewing a discussion.
   */
  viewingDiscussion(discussion) {
    return this.current.matches(DiscussionPage, {
      discussion
    });
  }
  /**
   * When the server reports an asset revision (on an API response) that differs
   * from the one this page booted with, the forum's JS/CSS has been rebuilt since
   * load. Prompt the user to reload to pick up the new assets, at most once.
   *
   * Both values are produced server-side (see `AssetsRevision`), so they are
   * directly comparable regardless of which versioner the forum uses.
   *
   * Public so the realtime extension can call it with a pushed revision token.
   */
  checkAssetsRevision(serverRevision) {
    var _this$data;
    const bootedRevision = (_this$data = this.data) == null ? void 0 : _this$data.assetsRevision;
    if (!serverRevision || !bootedRevision || serverRevision === bootedRevision || this.assetsRevisionAlertShown) {
      return;
    }
    this.assetsRevisionAlertShown = true;
    this.alerts.show({
      type: 'warning',
      dismissible: true,
      controls: [m(Button, {
        className: "Button Button--link",
        onclick: () => window.location.reload()
      }, app.translator.trans('core.lib.assets_updated.reload_button'))]
    }, app.translator.trans('core.lib.assets_updated.message'));
  }
}